import { useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, STUDIO } from "@/lib/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Messaggio inviato! Ti risponderò al più presto.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Errore durante l'invio del messaggio");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="py-20 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <span className="eyebrow" data-testid="contact-eyebrow">Contatti</span>
            <h2
              className="font-serif-elegant mt-6 text-3xl lg:text-5xl font-light tracking-tight text-[#3D3B3A] leading-tight"
              data-testid="contact-title"
            >
              Scrivimi,<br />
              <span className="italic text-[#6B8568]">ti rispondo</span>.
            </h2>
            <p className="mt-6 text-[#73706E] leading-relaxed">
              Per informazioni, domande o per fissare un primo colloquio
              conoscitivo. Mi prenderò il tempo di leggerti con attenzione.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-address">
                <MapPin size={20} strokeWidth={1.5} className="text-[#9DB39A] mt-1" />
                <div>
                  <p className="eyebrow">Studio</p>
                  <p className="text-sm text-[#3D3B3A] mt-1">{STUDIO.address}</p>
                  <p className="text-sm text-[#3D3B3A]">{STUDIO.city}</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-phone">
                <Phone size={20} strokeWidth={1.5} className="text-[#9DB39A] mt-1" />
                <div>
                  <p className="eyebrow">Telefono</p>
                  <a href={`tel:${STUDIO.phoneRaw}`} className="text-sm text-[#3D3B3A] mt-1 block hover:text-[#7E9C7B]">
                    {STUDIO.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-email">
                <Mail size={20} strokeWidth={1.5} className="text-[#9DB39A] mt-1" />
                <div>
                  <p className="eyebrow">Email</p>
                  <a href={`mailto:${STUDIO.email}`} className="text-sm text-[#3D3B3A] mt-1 block hover:text-[#7E9C7B]">
                    {STUDIO.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-hours">
                <Clock size={20} strokeWidth={1.5} className="text-[#9DB39A] mt-1" />
                <div>
                  <p className="eyebrow">Orari</p>
                  <p className="text-sm text-[#3D3B3A] mt-1">Lun – Ven · 9:00 – 19:00</p>
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-2xl overflow-hidden border border-[#CFD9C5] aspect-[16/10]">
              <iframe
                title="Studio Chiara Maiolo - Mappa"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                src="https://www.openstreetmap.org/export/embed.html?bbox=7.88%2C45.18%2C7.91%2C45.20&layer=mapnik&marker=45.1900%2C7.8946"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="bg-[#E9EFE4] rounded-3xl p-8 lg:p-12 space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="cname" className="text-xs text-[#73706E]">Nome e cognome *</Label>
                <Input
                  id="cname"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 bg-transparent border-0 border-b border-[#9DB39A]/40 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                  required
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="cemail" className="text-xs text-[#73706E]">Email *</Label>
                  <Input
                    id="cemail"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 bg-transparent border-0 border-b border-[#9DB39A]/40 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="cphone" className="text-xs text-[#73706E]">Telefono</Label>
                  <Input
                    id="cphone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 bg-transparent border-0 border-b border-[#9DB39A]/40 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cmsg" className="text-xs text-[#73706E]">Messaggio *</Label>
                <Textarea
                  id="cmsg"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={6}
                  className="mt-2 bg-transparent border border-[#9DB39A]/40 rounded-xl focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                  placeholder="Raccontami brevemente cosa ti porta a scrivermi…"
                  required
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input
                  type="checkbox"
                  data-testid="contact-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 accent-[#9DB39A]"
                  required
                />
                <span className="text-xs text-[#73706E] leading-relaxed">
                  Ho letto e accetto la{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#7E9C7B] underline">
                    Privacy Policy
                  </a>{" "}
                  e acconsento al trattamento dei miei dati per rispondere alla richiesta. *
                </span>
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center bg-[#9DB39A] text-white hover:bg-[#7E9C7B] disabled:opacity-60 rounded-full px-8 py-4 text-sm tracking-wide transition-all duration-300"
              >
                {submitting ? "Invio in corso…" : "Invia messaggio"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
